/**
 * CardVisual — the illustrated credit card of REDESIGN §7c.
 *
 * A real card shape (aspect 1.586, radius 12) in the issuer's colour with a drawn chip, the
 * last four digits in tabular figures, the issuer monogram, a drawn network mark and an overlay
 * for חסום / נסגר. Everything is inline SVG in theme tokens; the marks are our own simple
 * shapes, never an issuer's or a network's artwork.
 *
 * Copy stays in the apps: pass `label` (the card's name) and `stateLabel` (the Hebrew status)
 * from `i18n/he.ts`. Only the last four digits are ever rendered — `lastFour()` throws the rest
 * of the PAN away before it can reach the DOM (ARCHITECTURE §0.6).
 */
import type { ReactNode } from 'react';
import { CARD_SIZES, cardBox, formatCardMask, lastFour, networkFromPan } from './card.ts';
import type { CardNetwork, CardSize, CardState } from './card.ts';
import { PROVIDER_MONOGRAMS, providerMarkId } from './providers.ts';
import './kit.css';

/** The drawing space: 317.2 × 200 is exactly 1.586, so `rx = 12` is 12px at the `lg` size. */
const VB_W = 317.2;
const VB_H = 200;
const RX = 12;

/** Bullet quads printed before the last four, by size. */
const MASK_GROUPS: Readonly<Record<CardSize, number>> = { sm: 2, md: 3, lg: 4 };

export interface CardVisualProps {
  /** Financy `providerId` of the issuer — decides the colour and the monogram. */
  providerId: string | null | undefined;
  /** Masked PAN or `***6044`; only its last four digits are used. */
  cardNumber?: string | null;
  /** The last four digits, when the caller already extracted them. Wins over `cardNumber`. */
  last4?: string | null;
  /** Network mark. Derived from `cardNumber` when omitted; `other` draws none. */
  network?: CardNetwork;
  /** `blocked` and `closed` draw the state overlay (`creditStatus` blocked / deleted). */
  state?: CardState;
  size?: CardSize;
  /** The card's name, printed under the digits (Hebrew, from the app). */
  label?: string;
  /** The Hebrew status word drawn across a blocked/closed card. */
  stateLabel?: string;
  /** Accessible name for the whole card. Without it the card is decorative. */
  title?: string;
  className?: string;
}

export function CardVisual({
  providerId,
  cardNumber,
  last4,
  network,
  state = 'active',
  size = 'md',
  label,
  stateLabel,
  title,
  className,
}: CardVisualProps) {
  const issuer = providerMarkId(providerId);
  const digits = last4 ?? lastFour(cardNumber);
  const mask = formatCardMask(digits, MASK_GROUPS[size]);
  const net = network ?? networkFromPan(cardNumber) ?? 'other';
  const box = cardBox(size);
  const labelled = typeof title === 'string' && title.length > 0;
  return (
    <svg
      className={`fk-cardvis fk-cardvis--${size} fk-cardvis--${issuer} fk-cardvis--${state}${className ? ` ${className}` : ''}`}
      width={box.width}
      height={box.height}
      viewBox={`0 0 ${VB_W} ${VB_H}`}
      role={labelled ? 'img' : undefined}
      aria-hidden={labelled ? undefined : true}
      focusable="false"
    >
      {labelled ? <title>{title}</title> : null}
      <rect className="fk-cardvis__face" width={VB_W} height={VB_H} rx={RX} />
      <path className="fk-cardvis__sheen" d={`M${VB_W} 44v78L146 200H58Z`} />

      {/* issuer monogram, top-left as on a real card */}
      <g className="fk-cardvis__monogram" transform="translate(24 18) scale(1.5)">
        <path d={PROVIDER_MONOGRAMS[issuer]} />
      </g>

      {/* the chip */}
      <g className="fk-cardvis__chip">
        <rect x="26" y="68" width="46" height="35" rx="6" />
        <path className="fk-cardvis__chip-lines" d="M26 79.7h46M26 91.3h46M49 68v35" />
      </g>

      {mask ? (
        <text className="fk-cardvis__digits" x="26" y="140">
          {mask}
        </text>
      ) : null}

      <NetworkMark network={net} />

      {label ? (
        <text className="fk-cardvis__label" x={VB_W - 24} y="176" textAnchor="end">
          {label}
        </text>
      ) : null}

      {state === 'active' ? null : (
        <g className="fk-cardvis__state">
          <rect className="fk-cardvis__scrim" width={VB_W} height={VB_H} rx={RX} />
          <g className="fk-cardvis__state-mark" transform={`translate(${VB_W / 2} 82)`}>
            {state === 'blocked' ? (
              <>
                <rect x="-11" y="-2" width="22" height="16" rx="3" />
                <path className="fk-cardvis__state-stroke" d="M-7.5 -2v-5a7.5 7.5 0 0 1 15 0v5" />
              </>
            ) : (
              <>
                <circle className="fk-cardvis__state-stroke" r="11" />
                <path className="fk-cardvis__state-stroke" d="M-7.8 7.8 7.8 -7.8" />
              </>
            )}
          </g>
          {stateLabel ? (
            <text className="fk-cardvis__state-label" x={VB_W / 2} y="126" textAnchor="middle">
              {stateLabel}
            </text>
          ) : null}
        </g>
      )}
    </svg>
  );
}

/** Visa and Mastercard drawn as simple shapes (§7c) — our own marks, not their artwork. */
function NetworkMark({ network }: { network: CardNetwork }) {
  if (network === 'mastercard') {
    return (
      <g className="fk-cardvis__net fk-cardvis__net--mc">
        <circle className="fk-cardvis__net-a" cx="44" cy="162" r="16" />
        <circle className="fk-cardvis__net-b" cx="66" cy="162" r="16" />
      </g>
    );
  }
  if (network === 'visa') {
    return (
      <g className="fk-cardvis__net fk-cardvis__net--visa">
        <path className="fk-cardvis__net-chevron" d="M32 148l12 28 12-28" />
        <path className="fk-cardvis__net-bar" d="M30 183h48" />
      </g>
    );
  }
  return null;
}

/**
 * The mobile stack of §7c: cards overlap, the last one is fully visible. Give it the cards as
 * children; each child should be a {@link CardVisual} (or a link wrapping one).
 */
export function CardStack({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={`fk-cardstack${className ? ` ${className}` : ''}`}>{children}</div>;
}

/** Re-exported so a caller can size a container without importing `card.ts` separately. */
export { CARD_SIZES };
